import { Alert, Col, Row, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useRulesForm from '../useRulesForm';
import { Badge, Toggle } from '../../FanWave/components/ui';
import RulesFields from './RulesFields';
import SaveButton from './SaveButton';

/**
 * The channel's override session: the same three parts as its defaults (starting from them),
 * plus a session length and, in Session & targets, the switch that runs it. Switching it on
 * pauses the running default session and starts one override session on these rules; when it
 * is over — time up, a target reached or switched off — the default session resumes and the
 * switch turns itself off. Backend: PUT /admin/goalrush/setManualSession.
 */
const OverridesSection = ({ teamId, manual, teamRules, onConfig }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const form = useRulesForm(manual?.resolved ?? teamRules);
  const enabled = !!manual?.enabled;
  // An override runs instead of the default session, so it needs GoalRush on (Defaults tab).
  const channelOn = !!teamRules?.enabled;
  // 'save' or 'toggle', whichever the admin is confirming / is in flight.
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const send = async (body, kind) => {
    setConfirm(null);
    setBusy(kind);
    const res = await Api.put('/admin/goalrush/setManualSession', { teamId, ...body }, null, {
      silent: true
    });
    setBusy(null);
    if (res?.data) {
      onConfig(res.data);
      form.markSaved(res.data.manual?.resolved ?? res.data.resolved);
      dispatch({ type: 'HANDLE_RES', payload: { res: t('admin.goalRush.saved') } });
    } else {
      dispatch({
        type: 'HANDLE_RES',
        payload: { err: res?.err || t('admin.goalRush.saveFailed') }
      });
    }
  };

  // The switch also saves any unsaved edits, so the session runs on what the form shows.
  const toggle = () =>
    send({ enabled: !enabled, ...(form.dirty ? { rules: form.patch } : {}) }, 'toggle');

  const switchRow = (
    <Col
      noFlex
      wid="100%"
      gap="8px"
      pad="14px 0 0"
      style={{ borderTop: `1px solid ${theme.GREY_20}` }}
    >
      <Row center between noFlex wid="100%" style={{ flexWrap: 'wrap' }} gap="12px">
        <Row center noFlex gap="8px">
          <Text b2 bold marg="0" style={{ fontSize: '13px' }}>
            {t('admin.goalRush.overrides.enable')}
          </Text>
          <Toggle
            on={enabled}
            disabled={!!busy || (!enabled && !channelOn)}
            onToggle={() => setConfirm('toggle')}
          />
        </Row>
        <Badge variant={enabled ? 'active' : 'neutral'} dot>
          {enabled ? t('admin.goalRush.overrides.on') : t('admin.goalRush.overrides.off')}
        </Badge>
      </Row>
      <Text c1 col={theme.SECONDARY} marg="0" style={{ fontSize: '12.5px' }}>
        {channelOn
          ? t('admin.goalRush.overrides.warning')
          : t('admin.goalRush.overrides.channelOff')}
      </Text>
    </Col>
  );

  return (
    <>
      <RulesFields
        draft={form.draft}
        errors={form.errors}
        onChange={form.onChange}
        sessionExtra={switchRow}
      />
      <SaveButton
        saving={busy === 'save'}
        disabled={!form.dirty || !form.valid || !!busy}
        onClick={() => setConfirm('save')}
      />

      {confirm && (
        <Alert
          text={
            confirm === 'toggle'
              ? enabled
                ? t('admin.goalRush.overrides.confirmDisable')
                : t('admin.goalRush.overrides.confirmEnable')
              : t('admin.goalRush.overrides.confirmSave')
          }
          acceptText={t('admin.goalRush.confirm')}
          refuseText={t('admin.goalRush.cancel')}
          accept={() => (confirm === 'toggle' ? toggle() : send({ rules: form.patch }, 'save'))}
          refuse={() => setConfirm(null)}
        />
      )}
    </>
  );
};

export default OverridesSection;
